import { useEffect } from 'react';
import { useChatContextValue } from './ChatContext';
import type { ChatPageContext } from './types';

/**
 * Publish the current page's chat context. Re-publishes whenever the (serialized)
 * context changes and clears it on unmount so a stale snapshot never leaks to
 * another page.
 *
 * Single-publisher assumption: this relies on exactly ONE tool page being mounted
 * at a time (the router unmounts the old page before the new one mounts, and the
 * cleanup clears before the next publish wins). When Part 2 adds more publishing
 * pages, keep that invariant — or namespace the context by a per-page key — so a
 * stale page can never clear/overwrite the active one (last-writer-wins).
 */
export function usePublishChatContext(ctx: ChatPageContext): void {
  const { setContext } = useChatContextValue();
  const key = JSON.stringify(ctx);
  useEffect(() => {
    setContext(ctx);
    return () => setContext(null);
    // ctx is captured via `key`; setContext is stable (useCallback).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, setContext]);
}
